import { useCallback, useEffect, useRef } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  getListings,
  getMyKitchenListings,
  getMyListingsCounts,
  getListingDetail,
  searchLocalListings,
  createListing,
  patchListing,
  deleteListing,
  uploadListingImage,
  getImagePlaceholders,
  createShareLink,
  resolveShareLink,
  generateListing,
  CreateListingRequest,
  PatchListingRequest,
  GenerateListingRequest,
  SearchLocalListingsParams,
  SearchLocalListingsFilters,
  SearchLocalListingsResponse,
  MyKitchenTab,
} from '../services/listingService';
import type { DishListing } from '../types/listing';
import { useLocationStore } from '../store/useLocationStore';
import { useBrowseAnchor } from './useBrowseAnchor';
import { applyLocaleMigration } from '../services/localeMigration';

// Distance / drive-time are exposed ONLY when the user's location came from
// device geolocation (see useLocationStore.LocationSource). For any other
// source we null these out at the data layer so the value never reaches the
// UI — DishInfoCard then renders the "no location" affordance instead.
const stripDistance = (l: DishListing): DishListing => ({
  ...l,
  distance: null,
  driveTime: null,
});

/** True when the current location is device-sourced (distance allowed). */
function useDistanceAllowed(): boolean {
  return useLocationStore((s) => s.locationSource === 'device');
}

// `/listings/mine` is user-scoped (needs auth). Callers in an authenticated
// context can omit `enabled`; the logged-out publish flow passes `false` so we
// don't fire an authenticated GET before the user has an account.
export function useListings(enabled = true) {
  return useQuery({
    queryKey: ['listings'],
    queryFn: getListings,
    enabled,
  });
}

// My Kitchen page size for cursor pagination.
const MY_KITCHEN_PAGE_SIZE = 20;

/**
 * One My Kitchen tab's listings, cursor-paginated for infinite scroll. Keyed
 * by tab so each tab is its own cache entry. Mutations that touch `['listings']`
 * (create/patch/delete) also invalidate this via the shared prefix.
 */
export function useMyKitchenListings(status: MyKitchenTab) {
  return useInfiniteQuery({
    queryKey: ['listings', 'mine', status],
    queryFn: ({ pageParam }) =>
      getMyKitchenListings({
        status,
        fetchSize: MY_KITCHEN_PAGE_SIZE,
        lastListingId: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.length === MY_KITCHEN_PAGE_SIZE
        ? lastPage[lastPage.length - 1].id
        : undefined,
  });
}

/**
 * Per-tab listing counts, for picking the default My Kitchen tab. Also used by
 * ProfileMenu to decide whether to show the My Kitchen row at all, which is why
 * `enabled` exists: the menu mounts at the app root for logged-out users too,
 * and `/listings/mine/counts` is user-scoped.
 */
export function useMyListingsCounts(enabled = true) {
  return useQuery({
    queryKey: ['listings', 'mine', 'counts'],
    queryFn: getMyListingsCounts,
    enabled,
  });
}

// List feed: 20-per-page matches the BE default (capped at 100). Pages are
// flattened by the consumer; `getNextPageParam` returns undefined once
// loaded count reaches the server's reported totalCount.
const LOCAL_LISTINGS_PAGE_SIZE = 20;

export function useLocalListings(
  params: SearchLocalListingsParams | null,
  filters: SearchLocalListingsFilters = {},
) {
  const distanceAllowed = useDistanceAllowed();
  const select = useCallback(
    (data: InfiniteData<SearchLocalListingsResponse>) =>
      distanceAllowed
        ? data
        : {
            ...data,
            pages: data.pages.map((p) => ({
              ...p,
              listings: p.listings.map(stripDistance),
            })),
          },
    [distanceAllowed],
  );
  const query = useInfiniteQuery({
    queryKey: ['local-listings', params, filters],
    queryFn: ({ pageParam }) => {
      // refetch() bypasses `enabled` — never send a request without a locale
      // (it would go out as `locale=undefined` and 500).
      if (!params?.localeId) {
        return Promise.resolve({
          listings: [],
          page: 1,
          pageSize: LOCAL_LISTINGS_PAGE_SIZE,
          totalCount: 0,
        });
      }
      return searchLocalListings(
        { ...params, page: pageParam, pageSize: LOCAL_LISTINGS_PAGE_SIZE },
        filters,
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.pageSize;
      return loaded < lastPage.totalCount ? lastPage.page + 1 : undefined;
    },
    enabled: params != null && !!params.localeId,
    select,
  });

  // Reconcile if the BE told us the requested locale was soft-deleted and
  // spatially migrated to a successor — the listings we just received are
  // already for the successor, so we just need to move the store off the
  // old id. `applyLocaleMigration` internally dedupes against the WS event
  // path (which may have already fired, or may fire concurrently).
  const migratedTo = query.data?.pages?.[0]?.localeMigrated;
  const requestedLocaleId = params?.localeId;
  useEffect(() => {
    if (requestedLocaleId && migratedTo) {
      void applyLocaleMigration(requestedLocaleId, migratedTo);
    }
  }, [requestedLocaleId, migratedTo]);

  return query;
}

// Map feed: the BE caps each request at pageSize=100, so for locales with
// more than 100 listings we walk pages client-side until totalCount is
// covered. Gated by `enabled` (set from `mapOpen`) so the loop only runs
// when the map modal is actually open.
const MAP_LISTINGS_PAGE_SIZE = 100;

export function useLocalListingsForMap(
  params: SearchLocalListingsParams | null,
  filters: SearchLocalListingsFilters = {},
  enabled: boolean,
) {
  const distanceAllowed = useDistanceAllowed();
  const select = useCallback(
    (listings: DishListing[]) =>
      distanceAllowed ? listings : listings.map(stripDistance),
    [distanceAllowed],
  );
  return useQuery({
    queryKey: ['local-listings-map', params, filters],
    queryFn: async () => {
      // refetch() bypasses `enabled` — never send a request without a locale.
      if (!params?.localeId) return [];
      const all: DishListing[] = [];
      let page = 1;
      const requestedLocaleId = params.localeId;
      while (true) {
        const resp = await searchLocalListings(
          { ...params, page, pageSize: MAP_LISTINGS_PAGE_SIZE },
          filters,
        );
        all.push(...resp.listings);
        // Fire-and-forget reconciliation if the BE flagged this locale as
        // migrated — the helper dedupes so calling it on every page (or
        // vs the WS event) is safe. Kicked off inline rather than via a
        // useEffect on data because we flatten pages into a plain array
        // before returning; the marker would be lost otherwise.
        if (resp.localeMigrated) {
          void applyLocaleMigration(requestedLocaleId, resp.localeMigrated);
        }
        const loaded = resp.page * resp.pageSize;
        if (loaded >= resp.totalCount || resp.listings.length === 0) break;
        page += 1;
      }
      return all;
    },
    enabled: enabled && params != null && !!params.localeId,
    select,
    // One retry instead of the app default of three: the map sheet shows a
    // failure overlay with its own retry, and the default backoff would keep
    // the user on a spinner for ~7s before getting there.
    retry: 1,
  });
}

/**
 * `trackView` marks the caller as a person opening the dish (the dish page),
 * which the BE counts as a listing view. Other callers share the cache entry
 * but never count.
 *
 * One view per OPEN: only the first fetch after the page opens a dish carries
 * the tracking signal. Refetches — window focus, reconnect, a moved browse
 * anchor, WS-driven refreshes — are not someone opening the dish again. A
 * tracked caller always refetches on mount, so a copy cached by an untracked
 * caller cannot swallow the view.
 */
export function useListingDetail(id: string | undefined, opts: { trackView?: boolean; quiet404?: boolean } = {}) {
  const trackView = !!opts.trackView;
  // A missing listing skips the global error modal (see useOrderDetail).
  const quiet404 = !!opts.quiet404;
  // The dish this mount has already recorded a view for. Resets when the page
  // opens a different dish (or remounts).
  const viewRecordedFor = useRef<string | null>(null);
  const distanceAllowed = useDistanceAllowed();
  // Same reference point the feed search sends, so the distance on a dish's
  // detail page matches the one on its feed card. Part of the cache key: move
  // the user and the cached detail is refetched against the new anchor rather
  // than serving miles measured from where they used to be.
  const anchor = useBrowseAnchor();
  const select = useCallback(
    (listing: DishListing) => (distanceAllowed ? listing : stripDistance(listing)),
    [distanceAllowed],
  );
  return useQuery({
    queryKey: ['listing', id, anchor],
    queryFn: async () => {
      const track = trackView && viewRecordedFor.current !== id;
      // Claimed before the request so two overlapping fetches can't both count;
      // released on failure so the retry still records the open.
      if (track) viewRecordedFor.current = id!;
      try {
        return await getListingDetail(id!, anchor, { trackView: track, quiet404 });
      } catch (err) {
        if (track) viewRecordedFor.current = null;
        throw err;
      }
    },
    enabled: !!id,
    refetchOnMount: trackView ? 'always' : true,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    select,
  });
}

export function useCreateListing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateListingRequest) => createListing(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
    },
  });
}

export function usePatchListing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      fields,
      quietStaleFetch,
    }: {
      id: string;
      fields: PatchListingRequest;
      quietStaleFetch?: boolean;
    }) => patchListing(id, fields, { quietStaleFetch }),
    onSuccess: (data: DishListing) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
      queryClient.invalidateQueries({ queryKey: ['listing', data.id] });
    },
  });
}

type ListingPatchBatch = {
  id: string;
  fields: PatchListingRequest;
  waiters: { resolve: (listing: DishListing) => void; reject: (e: unknown) => void }[];
};

function isStaleFetch(e: unknown): boolean {
  const err = e as { status?: number; errorCode?: string } | null;
  return err?.status === 409 && err?.errorCode === 'STALE_FETCH';
}

/**
 * PATCH /listings/{id}, one request at a time, for a form that autosaves on
 * every field change.
 *
 * The backend pins each listing/dish write to the row version it read at the
 * start of the request, so two PATCHes from the same tab that overlap on the
 * server refuse each other with 409 STALE_FETCH ("Someone else just updated
 * this"). Firing them in parallel also let them land out of order: two quick
 * tag taps could leave the server holding the first tap's array.
 *
 * So only one PATCH is in flight. Fields enqueued meanwhile merge into the next
 * one (later values win), and every caller's promise settles with the response
 * of the request that carried its fields. A STALE_FETCH (a different writer,
 * e.g. the AI generate flow, moved the row) is resent once: the patch is a
 * delta the server re-applies onto a fresh read, and nothing else from this
 * queue is in flight, so the resend can't reorder anything. The first attempt
 * is quiet; a second refusal reaches the global error modal as usual.
 */
export function useListingPatchQueue() {
  const patchMutation = usePatchListing();
  const mutateRef = useRef(patchMutation.mutateAsync);
  mutateRef.current = patchMutation.mutateAsync;

  const queue = useRef<ListingPatchBatch[]>([]);
  const running = useRef(false);
  const idleWaiters = useRef<(() => void)[]>([]);

  const run = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      while (queue.current.length > 0) {
        // Shifted before sending, so anything enqueued while this is in flight
        // starts or joins the next batch rather than mutating this one.
        const batch = queue.current.shift()!;
        try {
          let res: DishListing;
          try {
            res = await mutateRef.current({ id: batch.id, fields: batch.fields, quietStaleFetch: true });
          } catch (e) {
            if (!isStaleFetch(e)) throw e;
            res = await mutateRef.current({ id: batch.id, fields: batch.fields });
          }
          batch.waiters.forEach((w) => w.resolve(res));
        } catch (e) {
          batch.waiters.forEach((w) => w.reject(e));
        }
      }
    } finally {
      running.current = false;
      const waiters = idleWaiters.current;
      idleWaiters.current = [];
      waiters.forEach((resolve) => resolve());
    }
  }, []);

  /** Queue `fields` for listing `id`. Resolves with the server's listing once
   *  the request carrying them succeeds; rejects if it fails. */
  const enqueue = useCallback(
    (id: string, fields: PatchListingRequest) =>
      new Promise<DishListing>((resolve, reject) => {
        const last = queue.current[queue.current.length - 1];
        if (last && last.id === id) {
          last.fields = { ...last.fields, ...fields };
          last.waiters.push({ resolve, reject });
        } else {
          queue.current.push({ id, fields: { ...fields }, waiters: [{ resolve, reject }] });
        }
        void run();
      }),
    [run],
  );

  /** Resolves once nothing is queued or in flight. Never rejects: a failed
   *  save must not block the caller (e.g. the publish replay) from going on. */
  const drain = useCallback(() => {
    if (!running.current && queue.current.length === 0) return Promise.resolve();
    return new Promise<void>((resolve) => idleWaiters.current.push(resolve));
  }, []);

  return { enqueue, drain, isPending: patchMutation.isPending };
}

export function useDeleteListing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteListing(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
      queryClient.removeQueries({ queryKey: ['listing', id] });
    },
  });
}

export function useUploadListingImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listingId, file }: { listingId: string; file: File }) =>
      uploadListingImage(listingId, file),
    onSuccess: (data: DishListing) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
      queryClient.invalidateQueries({ queryKey: ['listing', data.id] });
    },
  });
}

export function useResolveShareLink(ref: string | null) {
  return useQuery({
    queryKey: ['share-link', ref],
    queryFn: () => resolveShareLink(ref!),
    enabled: !!ref,
    retry: false,
  });
}

export function useCreateShareLink() {
  return useMutation({
    mutationFn: ({
      listingId,
      route,
      qr,
    }: {
      listingId?: string;
      route: string;
      qr?: 'primary' | 'secondary';
    }) => createShareLink(listingId, { route }, qr),
  });
}

export function useGenerateListing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, serviceKey, ...body }: GenerateListingRequest & { signal?: AbortSignal; serviceKey?: string }) =>
      generateListing(body, signal, serviceKey),
    onSuccess: (_data, variables) => {
      if (variables.listingId) {
        queryClient.invalidateQueries({ queryKey: ['listing', variables.listingId] });
      }
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
    },
  });
}

// `/listings/image-placeholders` requires auth. Callers in an authed context
// can omit `enabled`; the logged-out publish flow passes `false` so the browse
// catalog doesn't fire an unauthenticated request (logged-out users get their
// image from AI generation instead).
export function useImagePlaceholders(enabled = true, serviceKey?: string) {
  return useQuery({
    queryKey: ['image-placeholders'],
    queryFn: () => getImagePlaceholders(undefined, undefined, serviceKey),
    enabled,
  });
}
